import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * ### 入所評価判定テンプレート API 操作クラス
 */
export default class AdmissionJudgementTemplates {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET AdmissionJudgementTemplates
   */
  public async get(userStore: LoginUser): Promise<AdmissionJudgementTemplatesGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.AdmissionJudgementTemplate, formatParams);

    const response = await this.commonService.executeGet<AdmissionJudgementTemplatesGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET AdmissionJudgementTemplates
   */
  public async gets(userStore: LoginUser): Promise<Array<AdmissionJudgementTemplatesGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.AdmissionJudgementTemplate, formatParams);

    const response = await this.commonService.executeGet<Array<AdmissionJudgementTemplatesGetModel>>(
      userStore,
      `${requestUrl}/all`,
      this.constructor.name,
      {},
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }
}

export interface AdmissionJudgementTemplatesGetModel {
  /** システムで一意に特定するための ID */
  id: number;
  /** テンプレート名 */
  name: string;
  /** 説明文 */
  information: string;
  /** 全施設で使用可能か否か */
  canUseAll: boolean;
  /** 施設 ID を配列で持つ */
  canUseFacilities: number[];
  /** テンプレート情報の Object */
  template: Template;
  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 更新日時 (YYYYMMDDhhmmssSSS) */
  updated: number;
  /** 作成日時 (YYYYMMDDhhmmssSSS) */
  created: number;
}

export interface Template {
  /** テンプレートアイテム */
  properties: Array<TemplateItem>;
}

export interface TemplateItem {
  /** テンプレート種別 */
  type: TemplateType;
  /** テンプレート内で一意の ID */
  id: string;
  /** 見出し */
  label: string;
  /** 見出し表示可否 */
  showLabel: boolean;
  /** 説明文 */
  text?: string;
  /** 強調説明文 */
  strongText: string;
  /** ヘルプテキスト */
  helpText: string;
  /** ユーザーに表示するか否か */
  displayToUser: boolean;
}

export enum TemplateType {
  SECTION = "section",
  TEXT = "text",
  RADIO = "radio",
  RADIO_GROUP = "radioGroup",
  MULTIPLE_CHECKBOX = "multipleCheckbox"
}

export interface TemplateItemSection extends TemplateItem {
  /** テンプレートアイテム */
  properties: Array<TemplateItem>;
}

export interface TemplateItemInput extends TemplateItem {
  /** バリデーションルール */
  validationRules: string[];
  /** 参照先ID */
  referenceId: string;
}

export interface TemplateItemText extends TemplateItemInput {
  /** placeholder 属性 */
  placeholder?: string;
  /** type 属性。type 属性で入力可能なものは全て使用可能 */
  inputType?: string;
  /** max 属性 */
  max?: number;
  /** min 属性 */
  min?: number;
  /** maxlength 属性 */
  maxlength?: number;
  /** minlength 属性 */
  minlength?: number;
  /** 入力項目の前に付与する文字列 */
  prefix?: string;
  /** 入力項目の後に付与する文字列 */
  suffix?: string;
  /** width 属性 */
  width: number;

  /** 計算種別 */
  calcType: CalcType;
  /** 点数を表示するか否か */
  showPoint: boolean;

  /** 回答入力用のプロパティ。回答の初期値としても使用可能 */
  answer: string;
}

export interface TemplateItemRadio extends TemplateItemInput {
  /** 選択肢 */
  options: Array<RadioOption>;
  /** 配置の向き */
  align: RadioAlign;

  /** 計算種別 */
  calcType: CalcType;
  /** 点数を表示するか否か */
  showPoint: boolean;

  /** 回答入力用のプロパティ。回答の初期値としても使用可能 */
  answer: string;
}

export interface RadioOption {
  /** 画面表示項目名 */
  text: string;
  /** 値 */
  value: string;
  /** 入居判定の点数 */
  point: string | null;
}

export enum RadioAlign {
  VERTICAL = "vertical",
  HORIZONTAL = "horizontal"
}

export interface TemplateItemMultipleCheckbox extends TemplateItemInput {
  /** 質問内容 */
  questions: string[];
  /** 回答入力用のプロパティ。回答の初期値としても使用可能 */
  answer: boolean[];

  /** 点数 */
  points: string[];
  /** 点数上限 */
  limitPoint?: number | null;
  /** 計算種別 */
  calcType: CalcType;
  /** 点数を表示するか否か */
  showPoint: boolean;
}

export interface TemplateItemRadioGroup extends TemplateItemInput {
  /** テンプレートアイテム */
  properties: Array<RadioGroup>;

  /** 回答入力用のプロパティ。回答の初期値としても使用可能 */
  answer: string;
}

export interface RadioGroup {
  /** 選択肢 */
  option: RadioOption;

  /** テンプレートアイテム */
  properties: Array<TemplateItem>;
}

export enum CalcType {
  ADD = "add",
  REPLACE = "replace",
  CALC_ADD = "calcAdd"
}
